/**
 * « Travail demandé » : ce qui est à faire, jour par jour (§6.12).
 *
 * Demandé par Hedi le 15 septembre 2026 : *« Dans l'ecran des parent, je veux
 * une sous-tab Travail demandé avec la liste du travail demandé et des
 * évaluations annoncées organisées par jour (demain, apres-demain, le jour
 * d'après, … ) »*.
 *
 * **Les deux sources existaient et ne s'étaient jamais rencontrées.** Le cahier
 * de textes nourrissait le plan du soir de l'enfant, les évaluations annoncées
 * partaient sur Telegram une semaine avant l'épreuve : aucun écran ne montrait
 * *l'échéancier*, c'est-à-dire la seule forme sous laquelle un parent peut dire
 * « ce soir, c'est l'anglais de demain et le contrôle de jeudi ».
 *
 * **Une journée, deux natures.** Une ligne d'évaluation porte une étiquette
 * colorée, et elle dit *laquelle des deux* : déclarée par le professeur dans
 * EcoleDirecte, ou repérée dans le texte de la ligne. La nuance n'est pas un
 * détail d'affichage — la seconde peut se tromper, et un parent qui réorganise
 * une semaine autour d'un contrôle a le droit de savoir lequel est signé.
 *
 * **Pas de sélecteur de période**, à la différence des autres onglets : la
 * fenêtre est dérivée une fois par le serveur (`travail_demande.fenetre`), et
 * elle commence **avant** aujourd'hui, un devoir dû hier soir et non rendu
 * étant la chose la plus urgente de la liste. Deux fenêtres recalculées de part
 * et d'autre feraient lire « rien ce jour-là » sur un jour simplement hors de
 * l'une des deux.
 *
 * **Une liste vide a deux causes opposées** et l'écran les sépare, comme « Ce
 * que disent les professeurs » : les professeurs n'ont rien demandé, ou le
 * connecteur n'a pas tourné. D'où la date du dernier import, affichée même —
 * surtout — quand il n'y a rien.
 *
 * Règle 4 n'est pas en jeu : ce qui est ici est le verbatim d'un *professeur*,
 * que le parent lit déjà sur son propre compte EcoleDirecte. Le journal du soir
 * de l'enfant n'apparaît pas sur cet écran.
 */

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { JourneeDemandee, LigneDemandee, TravailDemande } from "@/api/schema";
import { ecartEnJours, enClair, enClairCourt, nomRelatif } from "@/ui/dates";
import { contenuLisible } from "@/ui/imagesDuCahier";
import { etiquette } from "@/ui/matieres";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

export function TravailDemandeScreen() {
  const { childId = "" } = useParams();
  const [corps, setCorps] = useState<TravailDemande | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    setErreur(null);
    void (async () => {
      try {
        const charge = await call("travailDemande", { params: { child_id: childId } });
        if (!annule) setCorps(charge);
      } catch (echec) {
        if (!annule) {
          setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      annule = true;
    };
  }, [childId]);

  const jours = corps?.jours ?? [];

  return (
    <Screen
      title="Travail demandé"
      lead={corps ? `Du ${enClair(corps.depuis)} au ${enClair(corps.jusqu_a)}` : undefined}
    >
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!corps && !erreur ? <Loading /> : null}

      {corps && jours.length === 0 ? (
        <Card>
          <Empty>Rien de demandé ni d'annoncé sur cette période.</Empty>
          <Synchro corps={corps} />
        </Card>
      ) : null}

      {corps
        ? jours.map((journee) => (
            <Journee key={journee.le} journee={journee} aujourdhui={corps.aujourd_hui} />
          ))
        : null}

      {corps && jours.length > 0 ? (
        <Card>
          <Synchro corps={corps} />
        </Card>
      ) : null}
    </Screen>
  );
}

/**
 * D'où viennent ces lignes, et de quand.
 *
 * La même phrase que « Ce que disent les professeurs », pour la même raison :
 * « rien cette semaine » et « le connecteur n'a pas tourné depuis mardi »
 * s'afficheraient sinon exactement pareil, et la seconde est une panne.
 */
function Synchro({ corps }: { corps: TravailDemande }) {
  if (!corps.derniere_synchro) {
    return (
      <p className="muted small">
        Aucune donnée reçue d'EcoleDirecte sur cette période. Ce n'est pas
        forcément un cahier vide : ce peut être la synchronisation qui n'a rien
        rapporté.
      </p>
    );
  }
  return (
    <p className="muted small">
      Reçu d'EcoleDirecte, dernière mise à jour le{" "}
      {enClair(corps.derniere_synchro)}.
    </p>
  );
}

/**
 * Le nom du jour, et la date sous laquelle le vérifier.
 *
 * « Demain » seul obligerait à compter pour savoir de quel jour on parle dès
 * qu'on regarde l'écran le lendemain matin ; la date seule ne dirait pas que
 * c'est demain. Au-delà d'après-demain, `nomRelatif` rend une chaîne vide et la
 * date reste seule — « le jour d'après » est faux à J+9.
 */
export function titreDuJour(le: string, aujourdhui: string): string {
  const nom = nomRelatif(le, aujourdhui);
  const date = enClairCourt(le);
  if (!nom) return date;
  return `${nom.charAt(0).toUpperCase()}${nom.slice(1)} · ${date}`;
}

function Journee({ journee, aujourdhui }: { journee: JourneeDemandee; aujourdhui: string }) {
  const lignes = journee.lignes ?? [];
  const ecart = ecartEnJours(aujourdhui, journee.le);
  const enRetard = ecart !== null && ecart < 0;
  return (
    <Card title={titreDuJour(journee.le, aujourdhui)} tone={enRetard ? "attention" : "neutral"}>
      {/* L'échéance passée est dite et non déduite : la fenêtre commence avant
          aujourd'hui exprès, et un jour d'hier au milieu de la liste se lirait
          autrement comme une erreur de date. */}
      {enRetard ? <p className="muted small">Échéance passée.</p> : null}
      {lignes.length === 0 ? (
        <Empty>Rien ce jour-là.</Empty>
      ) : (
        <ul className="demande__liste">
          {lignes.map((ligne) => (
            <Ligne key={ligne.source_id} ligne={ligne} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function Ligne({ ligne }: { ligne: LigneDemandee }) {
  const photos = ligne.photos ?? [];
  return (
    <li className="demande__ligne">
      <p className="demande__entete">
        {ligne.evaluation ? (
          <>
            <span className="chip" data-nature="evaluation">
              {ligne.declaree ? "évaluation annoncée" : "évaluation repérée"}
            </span>{" "}
          </>
        ) : null}
        <strong>{etiquette(ligne.matiere, ligne.libelle)}</strong>
        {ligne.donne_le ? (
          <span className="muted small"> · donné le {enClairCourt(ligne.donne_le)}</span>
        ) : null}
      </p>
      {/* Le texte du professeur, tel qu'il l'a écrit. `contenuLisible` ne le
          reformule pas : elle remplace les jetons d'image par une phrase quand
          il n'y a *que* des images, un « [image] » nu ressemblant à une panne. */}
      <p className="demande__texte">{contenuLisible(ligne.contenu)}</p>
      {ligne.evaluation && !ligne.declaree ? (
        <p className="muted small">
          Repérée dans le texte du professeur, non cochée par lui dans
          EcoleDirecte.
        </p>
      ) : null}
      {photos.length > 0 ? <Photos photos={photos} /> : null}
    </li>
  );
}

/**
 * Les photos de l'énoncé, prises par l'enfant.
 *
 * Elles disent de qui elles viennent : ce ne sont pas les images du professeur
 * de l'onglet voisin, c'est ce que l'enfant a photographié dans son cahier pour
 * que le tuteur sache sur quoi porte le devoir. L'URL est signée et expire
 * (règle 8) — la mettre en favori ne donne pas un accès permanent.
 */
function Photos({ photos }: { photos: NonNullable<LigneDemandee["photos"]> }) {
  return (
    <>
      <p className="muted small">
        {photos.length === 1
          ? "L'énoncé, photographié par votre enfant."
          : `L'énoncé, photographié par votre enfant (${photos.length} pages).`}
      </p>
      <ul className="demande__photos">
        {photos.map((photo, index) => (
          <li key={photo.id}>
            <a href={photo.url} target="_blank" rel="noreferrer">
              <img
                className="demande__photo"
                src={photo.url}
                alt={`Page ${index + 1} de l'énoncé`}
                loading="lazy"
              />
            </a>
          </li>
        ))}
      </ul>
    </>
  );
}
